import {alias, createModelSchema, custom, deserialize, serialize, SKIP} from "serializr";
import {Schema} from "@/common/Schema";
import {BaseObject} from "@/common/BaseObject";

import {HeroKind} from "../hero/HeroKind";
import {TriggerUtils} from "../trigger/core/TriggerUtils";
import {GuardConfig} from "../guard/GuardConfig";

export class PromptModel extends BaseObject {

    static schema() {
        return custom(
            (v) => {
                return v && !v.empty() ? serialize(PromptModel, v) : SKIP;
            },
            (v) => {
                return deserialize(PromptModel, v) || new PromptModel();
            }
        );
    }

    constructor() {
        super();

        this._id = null;
        this._key = null;
        this._name = null;
        this._hero = HeroKind.NONE;
        this._group = 0;
        this._guard = new GuardConfig();

        this._repeat = 0;
        this._cooldown = 0;
        this._resetTime = 0;

        this._priority = 0;
        this._triggersAvailable = [];
        this._triggersComplete = [];
    }

    get id() {
        return this._id;
    }

    set id(value) {
        this._id = value;
    }

    get key() {
        return this._key;
    }

    set key(value) {
        this._key = value;
    }

    get name() {
        return this._name;
    }

    set name(value) {
        this._name = value;
    }

    get hero() {
        return this._hero;
    }

    set hero(value) {
        this._hero = value;
    }

    get group() {
        return this._group;
    }

    set group(value) {
        this._group = value;
    }

    get guard() {
        return this._guard;
    }

    set guard(value) {
        this._guard = value;
    }

    get repeat() {
        return this._repeat;
    }

    set repeat(value) {
        this._repeat = value;
    }

    get cooldown() {
        return this._cooldown;
    }

    set cooldown(value) {
        this._cooldown = value;
    }

    get resetTime() {
        return this._resetTime;
    }

    set resetTime(value) {
        this._resetTime = value;
    }

    get priority() {
        return this._priority;
    }

    set priority(value) {
        this._priority = value;
    }

    get triggersAvailable() {
        return this._triggersAvailable;
    }

    set triggersAvailable(value) {
        this._triggersAvailable = value;
    }

    get triggersComplete() {
        return this._triggersComplete;
    }

    set triggersComplete(value) {
        this._triggersComplete = value;
    }

    valid() {

        if (!HeroKind.is(this.hero)) {
            return false;
        }

        if (!Number.isInteger(this.group) || this.group < 0) {
            return false;
        }

        if (!Number.isInteger(this.repeat) || this.repeat < 0) {
            return false;
        }

        if (!Number.isInteger(this.cooldown) || this.cooldown < 0) {
            return false;
        }

        if (!Number.isInteger(this.resetTime) || this.resetTime < 0) {
            return false;
        }

        if (!Number.isInteger(this.priority) || this.priority < 0) {
            return false;
        }

        if (this.repeat > 0 && this.cooldown === 0) {
            return false;
        }

        if (this.guard && !this.guard.valid()) {
            return false;
        }

        if (this.triggersAvailable.some(v => !v || !v.valid())) {
            return false;
        }

        if (this.triggersComplete.some(v => !v || !v.valid())) {
            return false;
        }

        return true;
    }

    empty() {
        return false;
    }

    copy() {
        return super.copy();
    }

    clone(target = undefined) {
        if (!target) {
            return super.clone(new PromptModel());
        } else if (target instanceof PromptModel) {
            return super.clone(target);
        } else {
            console.assert(false);
            return undefined;
        }
    }
}

createModelSchema(PromptModel, {
    _id: alias("id", Schema.primitive()),
    _key: alias("key", Schema.primitive()),
    _name: alias("name", Schema.primitive()),
    _hero: alias("hero", HeroKind.schema()),
    _group: alias("group", Schema.primitive()),
    _guard: alias("guard", GuardConfig.schema()),
    _repeat: alias("repeat", Schema.primitive()),
    _cooldown: alias("cooldown", Schema.primitive()),
    _resetTime: alias("reset_time", Schema.primitive()),
    _priority: alias("priority", Schema.primitive()),
    _triggersAvailable: alias("triggers_available", TriggerUtils.schema()),
    _triggersComplete: alias("triggers_complete", TriggerUtils.schema())
});
